import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import logo from '@/assets/img/logo.svg';
import IconHelp from '@/components/IconHelp';
import NotificationModal from '@/components/Modals/NotificationModal';
import { sideMenuAnimation } from '@/components/SideMenu';
import { useUser } from '@/contexts/AuthContext';
import { useDrawerContext } from '@/contexts/DrawerContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { themeLight } from '@/themes/light';
import { INotification } from '@/types/api/notifications';
import { hasRoutersHiddenNavBar } from '@/utils';
import { ROUTER_PAGES } from '@/utils/constants';
import { isAuthenticatedOrFirstAccessPage } from '@/utils/validates';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  AppBar,
  IconButton,
  Grid,
  useMediaQuery,
  Button,
  Badge,
  Box,
} from '@mui/material';

export default function NavBar() {
  const mdUp = useMediaQuery(themeLight.breakpoints.up('md'));
  const { isDrawerOpen, toggleDrawerOpen } = useDrawerContext();
  const { notifications, setNotifications } = useNotifications();
  const [isOpenModalNotifications, setIsOpenModalNotifications] =
    useState(false);
  const { isAuthenticated } = useUser();
  const router = useRouter();

  const quantityUnreadNotifications =
    getUnreadNotifications(notifications).length;

  const isNotInFirstAccessPage = router.route !== ROUTER_PAGES.primeiroAcesso;

  function getUnreadNotifications(allNotifications: Array<INotification>) {
    return allNotifications.filter(({ read }) => !read);
  }

  return (
    <>
      {!hasRoutersHiddenNavBar(router, isAuthenticated) && (
        <>
          <AppBar
            position="fixed"
            sx={{
              zIndex: mdUp ? '999' : '',
              width: mdUp && isDrawerOpen ? 'calc(100% - 310px)' : '100%',
              ...sideMenuAnimation,
            }}
          >
            <Grid
              container
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 16px',
                height: '66px',
                textAlign: 'center',
              }}
            >
              <Grid item>
                {(isAuthenticatedOrFirstAccessPage(isAuthenticated, router) ||
                  !mdUp) && (
                  <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    onClick={toggleDrawerOpen}
                  >
                    {isDrawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
                  </IconButton>
                )}
              </Grid>
              <Link href={ROUTER_PAGES.home} passHref>
                <Grid
                  component="a"
                  item
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                >
                  <Image
                    height="26"
                    width="200"
                    src={logo}
                    alt="'Obra play' escrito em letras grandes e maiúsculas na cor branca"
                  />
                </Grid>
              </Link>
              <Grid item>
                {isAuthenticatedOrFirstAccessPage(isAuthenticated, router) ? (
                  <IconHelp />
                ) : (
                  mdUp && (
                    <Button
                      target="_blank"
                      href={ROUTER_PAGES.login()}
                      variant="outlined"
                      sx={{
                        color: 'white',
                        borderColor: 'white',
                        ':hover': { borderColor: '#FAFAFA' },
                      }}
                    >
                      entrar
                    </Button>
                  )
                )}
                {isAuthenticated ? (
                  <IconButton
                    onClick={() => {
                      setIsOpenModalNotifications(true);
                    }}
                    color="inherit"
                    aria-label="notifications"
                    edge="end"
                  >
                    <Badge
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      badgeContent={
                        quantityUnreadNotifications > 0 &&
                        quantityUnreadNotifications >= 10
                          ? '+9'
                          : quantityUnreadNotifications
                      }
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          width: '50%',
                        },
                      }}
                    >
                      <NotificationsNoneIcon />
                    </Badge>
                  </IconButton>
                ) : (
                  mdUp &&
                  isNotInFirstAccessPage && (
                    <Button
                      target="_blank"
                      href={ROUTER_PAGES.cadastro}
                      variant="outlined"
                      sx={{
                        backgroundColor: 'white',
                        mx: 2,
                        ':hover': { backgroundColor: '#FAFAFA' },
                      }}
                    >
                      cadastre-se
                    </Button>
                  )
                )}
              </Grid>

              <NotificationModal
                setNotifications={setNotifications}
                isOpen={isOpenModalNotifications}
                onClose={() => setIsOpenModalNotifications(false)}
                notifications={notifications}
              />
            </Grid>
          </AppBar>
          <Box sx={{ mb: 8 }} />
        </>
      )}
    </>
  );
}
